import { useMemo } from "react";
import { createStore, applyMiddleware } from "redux";
import thunk from "redux-thunk";
import { enableBatching } from "redux-batched-actions";
import { composeWithDevTools } from "redux-devtools-extension";
import { combineReducers } from "redux-immutable";
import { fromJS } from "immutable";
import keys from "lodash/keys";
import { clearJWT } from "utils/auth";
import Analytics from "utils/analytics";
import AUTH_CONST from "constants/auth";
import { initialState as initialApp, reducer as app } from "./app";
import { initialState as initialSession, reducer as session } from "./session";
import {
  initialState as initialApplication,
  reducer as application
} from "./application";
import { initialState as initialUser, reducer as user } from "./user";
import { initialState as initialAuth, reducer as auth } from "./auth";
import {
  initialState as initialOrganization,
  reducer as organization
} from "./organization";

let store;

const baseReducer = {
  app: initialApp,
  application: initialApplication,
  session: initialSession,
  auth: initialAuth,
  user: initialUser,
  organization: initialOrganization
};

export const reducerKeys = keys(baseReducer);
const exampleInitialState = fromJS(baseReducer);

function initStore(preloadedState = exampleInitialState) {
  const appReducer = combineReducers({
    app,
    application,
    session,
    auth,
    user,
    organization
  });

  const rootReducer = (state, action) => {
    /**
     * On Logout we need to:
     * - unset the reducers
     * - remove JWTs
     * - unset the 3PS registrations
     */
    if (action.type === AUTH_CONST.LOGOUT_USER) {
      clearJWT();
      Analytics.clearRegistrations();
      return appReducer(undefined, action);
    } else {
      return appReducer(state, action);
    }
  };

  return createStore(
    enableBatching(rootReducer),
    fromJS(preloadedState),
    composeWithDevTools(applyMiddleware(thunk))
  );
}

export const initializeStore = (preloadedState) => {
  let _store = store ?? initStore(preloadedState);

  // After navigating to a page with an initial Redux state, merge that state
  // with the current state in the store, and create a new store
  if (preloadedState && store) {
    _store = initStore({
      ...store.getState(),
      ...preloadedState
    });
    // Reset the current store
    store = undefined;
  }

  // For SSG and SSR always create a new store
  if (typeof window === "undefined") return _store;
  // Create the store once in the client
  if (!store) store = _store;

  return _store;
};

export function useStore(initialState) {
  const store = useMemo(() => initializeStore(initialState), [initialState]);
  return store;
}
